import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { safeLocalStorage } from '@/lib/storage-utils';
import { navigationItems as defaultNavigationItems } from '@/lib/navigation';
import { InvitationNotification } from '@/components/Sidebar/InvitationNotification';
import { supabase } from '@/lib/supabase';
import { SupabaseFileStorage } from '@/components/file-manager/utils/supabaseFileStorage';
import { cn } from '@/lib/utils';
import { PieChart as RechartsPieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import {
  Search,
  User,
  Settings,
  LogOut,
  ChevronRight,
  Sun,
  Moon,
  Monitor,
  CircleHelp as HelpCircle,
  PieChart,
  Users,
  Cloud,
} from 'lucide-react';

interface AppHeaderProps {
  isGlobalDarkMode: boolean;
  setIsGlobalDarkMode: (dark: boolean) => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  isGlobalDarkMode,
  setIsGlobalDarkMode,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, signOut } = useAuth();
  const { currentWorkspace, userRole } = useWorkspace();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const [showStorageMenu, setShowStorageMenu] = useState(false);
  const storageMenuRef = useRef<HTMLDivElement>(null);
  const storageButtonRef = useRef<HTMLButtonElement>(null);
  const [fileStorage, setFileStorage] = useState<{ used: number; total: number } | null>(null);
  const [backupStorage, setBackupStorage] = useState<{ used: number; total: number } | null>(null);
  const [loadingStorage, setLoadingStorage] = useState(false);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    if (showStorageMenu && currentWorkspace?.id) {
      if (!fileStorage && !backupStorage && !loadingStorage) {
        setLoadingStorage(true);
        Promise.all([
          SupabaseFileStorage.getStorageInfo(currentWorkspace.id).then(data => setFileStorage(data)),
          supabase.functions.invoke('r2-backups', {
            body: { action: 'WORKSPACE_STORAGE', workspaceId: currentWorkspace.id }
          }).then(({ data, error }) => {
            if (!error && data) setBackupStorage(data);
          })
        ]).finally(() => setLoadingStorage(false));
      }
    }
  }, [showStorageMenu, currentWorkspace?.id, fileStorage, backupStorage, loadingStorage]);

  // Derive the parent page title and icon from the current path
  const getPageInfo = () => {
    const pathname = location.pathname;

    // Direct section matches to parent navs
    if (pathname.startsWith('/sites')) {
      const sitesItem = defaultNavigationItems.find(item => item.id === 'sites');
      return { label: sitesItem?.label || 'Sites', icon: sitesItem?.icon || defaultNavigationItems[0].icon };
    }
    if (pathname.startsWith('/inbox')) {
      const inboxItem = defaultNavigationItems.find(item => item.id === 'inbox');
      return { label: inboxItem?.label || 'Inbox', icon: inboxItem?.icon || defaultNavigationItems[0].icon };
    }
    if (pathname.startsWith('/files')) {
      const filesItem = defaultNavigationItems.find(item => item.id === 'file-manager' || item.id === 'files');
      return { label: filesItem?.label || 'Files', icon: filesItem?.icon || defaultNavigationItems[0].icon };
    }

    // Compute visible navigation items (respecting workspace menu visibility)
    const baseHiddenIds = currentWorkspace?.appearance?.menu?.hiddenIds || [];
    const roleHiddenIds = currentWorkspace?.appearance?.menu?.roleHiddenIds || {};
    let hiddenIds = baseHiddenIds;
    if (userRole && userRole !== 'owner') {
      if (Array.isArray(roleHiddenIds[userRole])) {
        hiddenIds = roleHiddenIds[userRole];
      }
    }

    const navItems = defaultNavigationItems
      .map(item => {
        if (hiddenIds.includes(item.id)) return null;
        if (item.hasSubItems && item.subItems) {
          return { ...item, subItems: item.subItems.filter(sub => !hiddenIds.includes(sub.id)) };
        }
        return item;
      })
      .filter((item): item is typeof defaultNavigationItems[0] => item !== null);

    // Match parent nav item (either directly or via its sub-items)
    for (const item of navItems) {
      if (pathname === item.path || pathname.startsWith(item.path + '/')) {
        return { label: item.label, icon: item.icon };
      }
      if (item.hasSubItems && item.subItems) {
        for (const sub of item.subItems) {
          if (pathname === sub.path || pathname.startsWith(sub.path + '/')) {
            return { label: item.label, icon: item.icon }; // Return PARENT item label
          }
        }
      }
    }

    // Fallbacks for specific common route prefixes
    if (pathname.startsWith('/brand')) return { label: 'Brand', icon: defaultNavigationItems.find(i => i.id === 'brand')?.icon || defaultNavigationItems[0].icon };
    if (pathname.startsWith('/task-board') || pathname.startsWith('/boards') || pathname.startsWith('/projects') || pathname.startsWith('/timesheet')) {
      return { label: 'Boards', icon: defaultNavigationItems.find(i => i.id === 'boards')?.icon || defaultNavigationItems[0].icon };
    }
    if (pathname.startsWith('/crm') || pathname.startsWith('/deals') || pathname.startsWith('/proposals') || pathname.startsWith('/boards/proposals') || pathname.startsWith('/invoices')) {
      return { label: 'CRM', icon: defaultNavigationItems.find(i => i.id === 'crm')?.icon || defaultNavigationItems[0].icon };
    }
    if (pathname.startsWith('/services')) return { label: 'Services', icon: defaultNavigationItems.find(i => i.id === 'services')?.icon || defaultNavigationItems[0].icon };
    if (pathname.startsWith('/calendar')) return { label: 'Calendar', icon: defaultNavigationItems.find(i => i.id === 'calendar')?.icon || defaultNavigationItems[0].icon };

    return { label: 'Home', icon: defaultNavigationItems[0].icon };
  };

  const pageInfo = getPageInfo();

  // Close profile and storage menus on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(target) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(target)
      ) {
        setShowProfileMenu(false);
      }
      if (
        storageMenuRef.current &&
        !storageMenuRef.current.contains(target) &&
        storageButtonRef.current &&
        !storageButtonRef.current.contains(target)
      ) {
        setShowStorageMenu(false);
      }
    };

    if (showProfileMenu || showStorageMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu, showStorageMenu]);

  return (
    <header
      className={cn(
        'sticky top-0 h-14 flex items-center justify-between px-6 md:px-10 border-b shrink-0 z-40',
        isGlobalDarkMode
          ? 'bg-background border-border'
          : 'bg-white border-gray-200'
      )}
    >
      {/* Left: Page title */}
      <div className="flex items-center gap-2 flex-1 basis-0 min-w-0">
        <h1
          className={cn(
            'text-sm font-semibold tracking-tight truncate',
            isGlobalDarkMode ? 'text-foreground' : 'text-gray-900'
          )}
        >
          {pageInfo.label}
        </h1>
      </div>

      {/* Center: Search */}
      <div className="flex-1 flex justify-center px-4 max-w-2xl">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('open-global-search'))}
          className={cn(
            'flex items-center justify-between gap-2 h-9 px-3 w-full rounded-lg text-sm border transition-all cursor-pointer',
            isGlobalDarkMode
              ? 'bg-sidebar-accent/50 border-sidebar-border text-gray-400 hover:text-gray-200 hover:bg-sidebar-accent'
              : 'bg-gray-50 border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          )}
        >
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4" />
            <span className="hidden sm:inline">Search...</span>
          </div>
          <kbd
            className={cn(
              'hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium',
              isGlobalDarkMode
                ? 'bg-muted text-muted-foreground border-border'
                : 'bg-white text-gray-400 border-gray-200'
            )}
          >
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>
      </div>

      {/* Right: Storage, Notifications, Profile */}
      <div className="flex items-center gap-1 flex-1 basis-0 min-w-0 justify-end">

        {/* Storage */}
        <div className="relative">
          <button
            ref={storageButtonRef}
            onClick={() => setShowStorageMenu(!showStorageMenu)}
            className={cn(
              'h-9 px-3 rounded-lg flex items-center gap-2 transition-all duration-200 cursor-pointer mx-1 border',
              showStorageMenu
                ? isGlobalDarkMode
                  ? 'bg-sidebar-accent border-sidebar-border text-white'
                  : 'bg-gray-100 border-gray-200 text-gray-900'
                : isGlobalDarkMode
                  ? 'bg-sidebar-accent/50 border-sidebar-border text-gray-400 hover:text-gray-200 hover:bg-sidebar-accent'
                  : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            )}
            title="Storage"
          >
            <Cloud className="w-4 h-4" />
            <span className="text-sm font-medium hidden lg:block">Storage</span>
          </button>

          {showStorageMenu && (
            <div
              ref={storageMenuRef}
              className={cn(
                'absolute top-full right-0 mt-2 w-72 rounded-xl border shadow-2xl z-50 p-4',
                isGlobalDarkMode
                  ? 'bg-sidebar-accent border-sidebar-border'
                  : 'bg-white border-gray-200'
              )}
            >
              <div className="grid grid-cols-2 gap-4">
                {/* File Storage */}
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-2 mb-2 w-full justify-center">
                    <Cloud className={cn("w-4 h-4", isGlobalDarkMode ? "text-gray-400" : "text-gray-500")} />
                    <h3 className={cn("text-xs font-semibold uppercase tracking-wider", isGlobalDarkMode ? "text-gray-300" : "text-gray-600")}>File Storage</h3>
                  </div>
                  <div className="w-full h-32 relative">
                    {loadingStorage && !fileStorage ? (
                      <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">Loading...</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={[
                              { name: 'Used', value: fileStorage ? fileStorage.used : 0 },
                              { name: 'Free', value: fileStorage ? Math.max(0, fileStorage.total - fileStorage.used) : 1 }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={25}
                            outerRadius={45}
                            stroke="none"
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                          >
                            <Cell key="cell-used" fill="#3b82f6" />
                            <Cell key="cell-free" fill={isGlobalDarkMode ? "#374151" : "#f3f4f6"} />
                          </Pie>
                          <RechartsTooltip 
                            formatter={(value: number) => formatFileSize(value)}
                            contentStyle={{ 
                              backgroundColor: isGlobalDarkMode ? '#1f2937' : '#ffffff',
                              border: `1px solid ${isGlobalDarkMode ? '#374151' : '#e5e7eb'}`,
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  <p className={cn("text-[11px] font-medium mt-1 text-center", isGlobalDarkMode ? "text-gray-400" : "text-gray-500")}>
                    {fileStorage ? `${formatFileSize(fileStorage.used)} / ${formatFileSize(fileStorage.total)}` : 'Unavailable'}
                  </p>
                </div>
                
                {/* Site Backup Storage */}
                <div className="flex flex-col items-center">
                  <div className="flex items-center gap-2 mb-2 w-full justify-center">
                    <Cloud className={cn("w-4 h-4", isGlobalDarkMode ? "text-gray-400" : "text-gray-500")} />
                    <h3 className={cn("text-xs font-semibold uppercase tracking-wider", isGlobalDarkMode ? "text-gray-300" : "text-gray-600")}>Site Backups</h3>
                  </div>
                  <div className="w-full h-32 relative">
                    {loadingStorage && !backupStorage ? (
                      <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">Loading...</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={[
                              { name: 'Used', value: backupStorage ? backupStorage.used : 0 },
                              { name: 'Free', value: backupStorage ? Math.max(0, backupStorage.total - backupStorage.used) : 1 }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={25}
                            outerRadius={45}
                            stroke="none"
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                          >
                            <Cell key="cell-used-b" fill="#10b981" />
                            <Cell key="cell-free-b" fill={isGlobalDarkMode ? "#374151" : "#f3f4f6"} />
                          </Pie>
                          <RechartsTooltip 
                            formatter={(value: number) => formatFileSize(value)}
                            contentStyle={{ 
                              backgroundColor: isGlobalDarkMode ? '#1f2937' : '#ffffff',
                              border: `1px solid ${isGlobalDarkMode ? '#374151' : '#e5e7eb'}`,
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  <p className={cn("text-[11px] font-medium mt-1 text-center", isGlobalDarkMode ? "text-gray-400" : "text-gray-500")}>
                    {backupStorage ? `${formatFileSize(backupStorage.used)} / ${formatFileSize(backupStorage.total)}` : 'Unavailable'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notification bell */}
        <InvitationNotification
          isGlobalDarkMode={isGlobalDarkMode}
          align="right"
          verticalAlign="bottom"
        />

        {/* Profile avatar */}
        <div className="relative">
          <button
            ref={profileButtonRef}
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center overflow-hidden transition-all duration-200 ml-1 cursor-pointer',
              showProfileMenu
                ? isGlobalDarkMode
                  ? 'ring-2 ring-primary/50'
                  : 'ring-2 ring-primary/30'
                : 'hover:opacity-80'
            )}
          >
            <div
              className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center overflow-hidden',
                isGlobalDarkMode ? 'bg-sidebar-accent' : 'bg-gray-200'
              )}
            >
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User
                  className={cn(
                    'w-4 h-4',
                    isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'
                  )}
                />
              )}
            </div>
          </button>

          {/* Profile Dropdown */}
          {showProfileMenu && (
            <div
              ref={profileMenuRef}
              className={cn(
                'absolute top-full right-0 mt-2 w-80 rounded-xl border shadow-2xl z-50',
                isGlobalDarkMode
                  ? 'bg-sidebar-accent border-sidebar-border'
                  : 'bg-white border-gray-200'
              )}
            >
              {/* Accounts Section */}
              <div
                className={cn(
                  'p-3 border-b',
                  isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-100'
                )}
              >
                <p
                  className={cn(
                    'text-xs font-semibold mb-2 px-2',
                    isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'
                  )}
                >
                  Accounts
                </p>
                <button
                  onClick={() => {
                    navigate('?modal=settings&tab=profile');
                    setShowProfileMenu(false);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-lg transition-all group',
                    isGlobalDarkMode
                      ? 'hover:bg-sidebar'
                      : 'hover:bg-gray-50'
                  )}
                >
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0',
                      isGlobalDarkMode ? 'bg-sidebar' : 'bg-gray-200'
                    )}
                  >
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User
                        className={cn(
                          'w-5 h-5',
                          isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'
                        )}
                      />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p
                      className={cn(
                        'text-sm font-semibold',
                        isGlobalDarkMode ? 'text-white' : 'text-gray-900'
                      )}
                    >
                      {profile?.first_name || profile?.last_name
                        ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
                        : 'User Profile'}
                    </p>
                    {profile?.email && (
                      <p
                        className={cn(
                          'text-xs',
                          isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'
                        )}
                      >
                        {profile.email}
                      </p>
                    )}
                  </div>
                  <ChevronRight
                    className={cn(
                      'w-4 h-4',
                      isGlobalDarkMode ? 'text-gray-500' : 'text-gray-400'
                    )}
                  />
                </button>
              </div>

              {/* Workspace Section */}
              <div
                className={cn(
                  'p-3 border-b',
                  isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-100'
                )}
              >
                <p
                  className={cn(
                    'text-xs font-semibold mb-2 px-2',
                    isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'
                  )}
                >
                  Workspace
                </p>
                <button
                  onClick={() => {
                    navigate('?modal=settings&tab=workspace&workspaceTab=members');
                    setShowProfileMenu(false);
                  }}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 p-3 rounded-lg border transition-all',
                    isGlobalDarkMode
                      ? 'border-sidebar-border hover:bg-sidebar text-sidebar-foreground/80'
                      : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                  )}
                >
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">Manage Members</span>
                </button>
              </div>

              {/* Menu Items */}
              <div className="p-2">
                <button
                  onClick={() => {
                    navigate('?modal=settings&tab=profile');
                    setShowProfileMenu(false);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all',
                    isGlobalDarkMode
                      ? 'text-sidebar-foreground/80 hover:bg-sidebar'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <Settings className="w-4 h-4" />
                  <span className="font-medium">Settings</span>
                </button>

                {/* Theme toggle */}
                <div
                  className={cn(
                    'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm',
                    isGlobalDarkMode ? 'text-sidebar-foreground/80' : 'text-gray-700'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Monitor className="w-4 h-4" />
                    <span className="font-medium">Theme</span>
                  </div>
                  <button
                    aria-label="Toggle theme"
                    onClick={() => {
                      const next = !isGlobalDarkMode;
                      setIsGlobalDarkMode(next);
                      safeLocalStorage.setItem('themePreference', next ? 'dark' : 'light');
                    }}
                    className={cn(
                      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                      isGlobalDarkMode ? 'bg-indigo-600' : 'bg-gray-300'
                    )}
                  >
                    <span
                      className={cn(
                        'pointer-events-none inline-flex h-5 w-5 transform items-center justify-center rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                        isGlobalDarkMode ? 'translate-x-5' : 'translate-x-0'
                      )}
                    >
                      {isGlobalDarkMode ? (
                        <Moon className="w-3 h-3 text-indigo-600" />
                      ) : (
                        <Sun className="w-3 h-3 text-amber-500" />
                      )}
                    </span>
                  </button>
                </div>

                <button
                  className={cn(
                    'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm transition-all',
                    isGlobalDarkMode
                      ? 'text-sidebar-foreground/80 hover:bg-sidebar'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-4 h-4" />
                    <span className="font-medium">Help and resources</span>
                  </div>
                  <ChevronRight className="w-4 h-4 opacity-50" />
                </button>

                <button
                  onClick={() => {
                    navigate('?modal=settings&tab=billing');
                    setShowProfileMenu(false);
                  }}
                  className={cn(
                    'w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm transition-all',
                    isGlobalDarkMode
                      ? 'text-sidebar-foreground/80 hover:bg-sidebar'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <PieChart className="w-4 h-4" />
                    <span className="font-medium">Plans and pricing</span>
                  </div>
                  <ChevronRight className="w-4 h-4 opacity-50" />
                </button>
              </div>

              {/* Logout */}
              <div
                className={cn(
                  'p-2 border-t',
                  isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-100'
                )}
              >
                <button
                  onClick={async () => {
                    await signOut();
                    navigate('/login');
                    setShowProfileMenu(false);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all',
                    isGlobalDarkMode
                      ? 'text-sidebar-foreground/80 hover:text-red-400 hover:bg-red-500/10'
                      : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                  )}
                >
                  <LogOut className="w-4 h-4" />
                  <span className="font-medium">Log out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
